import {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  useState,
} from 'react';
import styled from 'styled-components';

/**
 * Persistent, top-aligned labels only. No floating or placeholder-as-label
 * inputs (a named AI tell). Shared styling across text, textarea and select.
 */

const FieldWrap = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const LabelRow = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const LabelText = styled.span`
  font-family: var(--screening-font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--screening-ink);
`;

const Optional = styled.span`
  font-size: 12px;
  color: var(--screening-muted);
`;

const Helper = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: var(--screening-muted);
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--screening-reject);
`;

const controlStyles = `
  font-family: var(--screening-font-ui);
  font-size: 15px;
  color: var(--screening-ink);
  background: var(--screening-paper);
  border: 1px solid var(--screening-hairline);
  border-radius: var(--screening-r-input);
  padding: 10px 12px;
  width: 100%;
  transition: border-color 160ms ease;

  &::placeholder {
    color: #9aa4ad;
  }
  &:hover:not(:disabled) {
    border-color: #cdd4da;
  }
  &:focus {
    outline: none;
    border-color: var(--screening-navy);
  }
  &:disabled {
    background: var(--screening-canvas);
    cursor: not-allowed;
  }
`;

const StyledInput = styled.input`
  ${controlStyles}
`;

const PasswordWrap = styled.span`
  position: relative;
  display: block;
  width: 100%;
`;

const PasswordInput = styled(StyledInput)`
  padding-right: 42px;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--screening-muted);
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    background: var(--screening-canvas);
    color: var(--screening-ink);
  }

  &:focus-visible {
    outline: 2px solid var(--screening-navy);
    outline-offset: 1px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const EyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const EyeOffIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M3 3l18 18"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M10.6 6.1c.46-.07.93-.1 1.4-.1 6.5 0 10 7 10 7a17.7 17.7 0 0 1-3.1 3.95M6.6 6.6C3.7 8.5 2 12 2 12s3.5 7 10 7c1.94 0 3.65-.5 5.1-1.28"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M9.9 9.9a3 3 0 0 0 4.2 4.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const StyledTextArea = styled.textarea<{ $minHeight: number }>`
  ${controlStyles}
  min-height: ${({ $minHeight }) => $minHeight}px;
  resize: vertical;
  line-height: 22px;
`;

const StyledSelect = styled.select`
  ${controlStyles}
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%235C6773' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 34px;
`;

interface CommonProps {
  label: string;
  optional?: boolean;
  helper?: string;
  error?: string;
}

const FieldFrame = ({
  label,
  optional,
  helper,
  error,
  children,
}: CommonProps & { children: ReactNode }) => (
  <FieldWrap>
    <LabelRow>
      <LabelText>{label}</LabelText>
      {optional && <Optional>Optional</Optional>}
    </LabelRow>
    {children}
    {error ? (
      <ErrorText>{error}</ErrorText>
    ) : (
      helper && <Helper>{helper}</Helper>
    )}
  </FieldWrap>
);

type TextFieldProps = CommonProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'children'>;

export const TextField = ({
  label,
  optional,
  helper,
  error,
  type,
  ...rest
}: TextFieldProps) => {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';

  return (
    <FieldFrame label={label} optional={optional} helper={helper} error={error}>
      {isPassword ? (
        <PasswordWrap>
          <PasswordInput {...rest} type={revealed ? 'text' : 'password'} />
          <ToggleButton
            type="button"
            onClick={() => setRevealed((current) => !current)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            disabled={rest.disabled}
          >
            {revealed ? EyeOffIcon : EyeIcon}
          </ToggleButton>
        </PasswordWrap>
      ) : (
        <StyledInput {...rest} type={type} />
      )}
    </FieldFrame>
  );
};

type TextAreaProps = CommonProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'> & {
    minRows?: number;
  };

const LINE_HEIGHT = 22;
const VERTICAL_PADDING = 20;

export const TextArea = ({
  label,
  optional,
  helper,
  error,
  minRows = 4,
  ...rest
}: TextAreaProps) => (
  <FieldFrame label={label} optional={optional} helper={helper} error={error}>
    <StyledTextArea
      {...rest}
      $minHeight={minRows * LINE_HEIGHT + VERTICAL_PADDING}
    />
  </FieldFrame>
);

type SelectFieldProps = CommonProps &
  SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode };

export const Select = ({
  label,
  optional,
  helper,
  error,
  children,
  ...rest
}: SelectFieldProps) => (
  <FieldFrame label={label} optional={optional} helper={helper} error={error}>
    <StyledSelect {...rest}>{children}</StyledSelect>
  </FieldFrame>
);
